import type { AdminApiUsagePeriod } from "@repo/app-types/entities/creditUsage";
import { DateTime } from "luxon";

/** Usage figures are fractional in the API; whole units everywhere on screen. */
export function formatWholeNumber(value: number): string {
  return Math.round(value).toLocaleString("en-US");
}

/** Whole credits, with a sliver of usage shown as "<1" rather than 0. */
export function formatCreditsUsed(credits: number): string {
  return credits > 0 && credits < 0.5 ? "<1" : formatWholeNumber(credits);
}

/** Usage no credit covered, as "1,200 uncovered"; null when there is none. */
export function formatOverage(overageCredits: number): string | null {
  return overageCredits > 0
    ? `${formatWholeNumber(overageCredits)} uncovered`
    : null;
}

/**
 * A usage figure against its allowance — "1,200 of 5,000 credits used", or
 * "1,200 credits used" when no allowance is set. The trailing verb is optional;
 * omit it for a bare "1,200 of 5,000".
 */
export function formatUsageAgainstIncluded(
  used: number,
  included: number | null,
  verb?: string,
): string {
  const base =
    included === null
      ? formatWholeNumber(used)
      : `${formatWholeNumber(used)} of ${formatWholeNumber(included)}`;
  return verb ? `${base} ${verb}` : base;
}

/**
 * A period as "Jul 13 – Aug 12, 2026". `ends_at` is exclusive, so the shown
 * end date is the day before it.
 */
export function formatPeriodLabel(period: AdminApiUsagePeriod): string {
  const start = DateTime.fromISO(period.starts_at, { zone: "utc" });
  const endInclusive = DateTime.fromISO(period.ends_at, { zone: "utc" }).minus({
    days: 1,
  });
  return `${start.toFormat("MMM d")} – ${endInclusive.toFormat("MMM d, yyyy")}`;
}

/** The day the period's allowance resets, i.e. its exclusive `ends_at`. */
export function formatPeriodResetDate(period: AdminApiUsagePeriod): string {
  return DateTime.fromISO(period.ends_at, { zone: "utc" }).toFormat(
    "MMM d, yyyy",
  );
}

/**
 * The last day a contract term covers, from its exclusive end — "Jan 12,
 * 2027". A term runs a year or more, so the year is always named.
 */
export function formatTermEndDate(termEndsAt: string): string {
  return DateTime.fromISO(termEndsAt, { zone: "utc" })
    .minus({ days: 1 })
    .toFormat("MMM d, yyyy");
}
